import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useToast } from "@/components/ui/use-toast";
import { ProductStoreProduct } from "../api/fetchProductStoreProducts/types";
import { createProductColumns } from "../components/ProductColumns/ProductColumns";
import {
  deleteProductStoreProductAction,
  updateProductStoreProductAction,
} from "../slices/productStoreProductsSlice";
import { useProductStoreProducts } from "./useProductStoreProducts";

export function useProductList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [refreshCounter, setRefreshCounter] = useState(0);
  const productsQuery = useProductStoreProducts(refreshCounter);

  const [deleteDialogState, setDeleteDialogState] = useState({
    isOpen: false,
    productId: null as string | null,
    productName: "",
  });

  const refreshList = useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  const handleEditProduct = useCallback(
    (product: ProductStoreProduct) => {
      navigate(`/lead-generation/product-store/products/${product.id}/edit`);
    },
    [navigate],
  );

  const handleTogglePublished = useCallback(
    async (product: ProductStoreProduct) => {
      try {
        await dispatch(
          updateProductStoreProductAction(product.id, {
            code: product.code,
            name: product.name,
            description: product.description,
            price: parseFloat(product.price),
            type: product.type,
            categoryId: product.category.id,
            isPublished: !product.isPublished,
          }),
        );
        refreshList();
      } catch (err) {
        toast({
          title: "Error",
          description:
            err instanceof Error ? err.message : "Failed to update product",
          variant: "destructive",
        });
      }
    },
    [dispatch, refreshList, toast],
  );

  const handleDeleteProduct = useCallback((product: ProductStoreProduct) => {
    setDeleteDialogState({
      isOpen: true,
      productId: product.id,
      productName: product.name,
    });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogState({
      isOpen: false,
      productId: null,
      productName: "",
    });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialogState.productId) return;

    try {
      await dispatch(
        deleteProductStoreProductAction(deleteDialogState.productId),
      );
      toast({
        title: "Success",
        description: "Product deleted successfully",
        variant: "default",
      });
      closeDeleteDialog();
      refreshList();
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to delete product",
        variant: "destructive",
      });
    }
  }, [
    dispatch,
    deleteDialogState.productId,
    closeDeleteDialog,
    refreshList,
    toast,
  ]);

  const goToCreateProduct = useCallback(() => {
    navigate("/lead-generation/product-store/products/new");
  }, [navigate]);

  const columns = useMemo(
    () =>
      createProductColumns(
        handleEditProduct,
        handleDeleteProduct,
        handleTogglePublished,
      ),
    [handleEditProduct, handleDeleteProduct, handleTogglePublished],
  );

  return {
    ...productsQuery,
    columns,
    deleteDialogState,
    closeDeleteDialog,
    handleConfirmDelete,
    goToCreateProduct,
  };
}
